'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  MessageCircle,
  Reply,
  Edit3,
  Loader2,
  Heart,
  Flag,
  Trash2,
  MoreHorizontal,
} from 'lucide-react';
import { useSession } from 'next-auth/react';
import { toast } from 'sonner';

import { useModalStore } from '@/store/modal.store';

import { Button } from '@/components/ui/button';
import { CreateComment } from '@/components/create-comment';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import type { Comment } from '@/types/comment';
import { cn } from '@/lib/utils';
import { COMMENT_REACTIONS } from '../constants/reactions';
import { formatCommentDate } from '../utils/format-comment-date.util';
import { getAuthorInitials } from '../utils/get-author-initials.util';
import { getCommentIndentClass } from '../utils/get-comment-indent-class.util';
import {
  canAuthorDeleteOwnComment,
  OWN_COMMENT_DELETE_WINDOW_MS,
} from '../utils/can-author-delete-own-comment.util';
import {
  canAuthorEditOwnComment,
  OWN_COMMENT_EDIT_WINDOW_MS,
} from '../utils/can-author-edit-own-comment.util';
import { CommentReportDialog } from './comment-report-dialog';
import type { PublicationKind } from '../types/publication-kind';
import { getPublicationCopy } from '../utils/publication-copy.util';

export type CommentItemProps = {
  comment: Comment;
  level?: number;
  articleId?: number;
  postId?: number;
  replyingTo: number | null;
  expandedReplies: Set<number>;
  repliesLoading: Set<number>;
  loadedReplies: Map<number, Comment[]>;
  reactingComments: Set<number>;
  localReactions: Map<number, Map<string, number>>;
  openReactionPopovers: Set<number>;
  resolveReactionCount: (commentId: number, reactionType: string, originalCount?: number) => number;
  resolveUserReaction: (commentId: number, originalUserReaction: string | null) => string | null;
  onReply: (commentId: number) => void;
  onCancelReply: () => void;
  onLoadReplies: (commentId: number) => void;
  onReplySubmit: (parentCommentId: number, newComment: Comment) => void;
  onReaction: (
    commentId: number,
    reactionType: string,
    currentUserReaction: string | null,
    originalReactionsCount: Record<string, number> | null | undefined,
  ) => void;
  onReactionPopoverOpenChange: (commentId: number, open: boolean) => void;
  onReport?: (
    commentId: number,
    category: string,
    reason: string,
    details?: string,
  ) => Promise<{ ok: boolean; message?: string }>;
  onDelete?: (commentId: number) => Promise<{ ok: boolean; message?: string }>;
  onUpdate?: (commentId: number, content: string) => Promise<{ ok: boolean; message?: string }>;
  publicationKind?: PublicationKind;
};

export function CommentItem({
  comment,
  level = 0,
  articleId,
  postId,
  replyingTo,
  expandedReplies,
  repliesLoading,
  loadedReplies,
  reactingComments,
  localReactions,
  openReactionPopovers,
  resolveReactionCount,
  resolveUserReaction,
  onReply,
  onCancelReply,
  onLoadReplies,
  onReplySubmit,
  onReaction,
  onReactionPopoverOpenChange,
  onReport,
  onDelete,
  onUpdate,
  publicationKind = 'comment',
}: CommentItemProps) {
  const { data: session } = useSession();
  const { setIsOpenAuthDialog } = useModalStore();
  const copy = getPublicationCopy(publicationKind);
  const isReply = level > 0 || Boolean(comment.parentComment);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editContent, setEditContent] = useState(comment.content || '');
  const [nowMs, setNowMs] = useState(() => Date.now());
  const indentClass = getCommentIndentClass(level);
  const currentUserId = session?.user?.id != null ? Number(session.user.id) : null;
  const isOwnComment =
    currentUserId != null &&
    comment.author?.id != null &&
    Number(comment.author.id) === currentUserId;
  const canReport = Boolean(onReport && comment.id && !isOwnComment);

  const repliesCount = useMemo(() => {
    const fromApi = comment.childrenCount?.totalDocs ?? 0;
    const fromLoaded = comment.id ? (loadedReplies.get(comment.id)?.length ?? 0) : 0;
    return Math.max(fromApi, fromLoaded);
  }, [comment.childrenCount?.totalDocs, comment.id, loadedReplies]);

  const canDelete = Boolean(
    onDelete &&
    comment.id &&
    isOwnComment &&
    canAuthorDeleteOwnComment(comment, { nowMs, repliesCount }),
  );
  const canEdit = Boolean(
    onUpdate && comment.id && isOwnComment && canAuthorEditOwnComment(comment, { nowMs }),
  );
  const showOwnerMenu = canEdit || canDelete;

  useEffect(() => {
    if (!isOwnComment || !comment.createdAt) return;
    const createdAtMs = new Date(comment.createdAt).getTime();
    if (!Number.isFinite(createdAtMs)) return;

    const timers: number[] = [];
    const schedule = (windowMs: number) => {
      const remaining = createdAtMs + windowMs - Date.now();
      if (remaining <= 0) return;
      timers.push(
        window.setTimeout(() => {
          setNowMs(Date.now());
        }, remaining + 50),
      );
    };
    schedule(OWN_COMMENT_DELETE_WINDOW_MS);
    schedule(OWN_COMMENT_EDIT_WINDOW_MS);

    return () => {
      for (const timer of timers) {
        window.clearTimeout(timer);
      }
    };
  }, [comment.createdAt, isOwnComment]);

  const currentUserReaction = comment.id
    ? resolveUserReaction(comment.id, comment.userReaction || null)
    : null;

  const openAuthForGuestReview = () => {
    if (publicationKind !== 'review' || session) return false;
    setIsOpenAuthDialog(true);
    return true;
  };

  const handleReplyClick = () => {
    if (!comment.id || openAuthForGuestReview()) return;
    onReply(comment.id);
  };

  const handleReportClick = () => {
    if (openAuthForGuestReview()) return;
    setReportDialogOpen(true);
  };

  const handleDeleteClick = async () => {
    if (!comment.id || !onDelete || isDeleting) return;
    const confirmed = window.confirm(copy.deleteConfirm(isReply));
    if (!confirmed) return;
    setIsDeleting(true);
    try {
      const result = await onDelete(comment.id);
      if (result.ok) {
        toast.success(copy.deleted(isReply));
      } else {
        toast.error(result.message || copy.deleteError);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStartEdit = () => {
    setEditContent(comment.content || '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditContent(comment.content || '');
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!comment.id || !onUpdate || isSavingEdit) return;
    const nextContent = editContent.trim();
    if (!nextContent) {
      toast.error(copy.emptyContent(isReply));
      return;
    }
    if (nextContent === (comment.content || '').trim()) {
      setIsEditing(false);
      return;
    }

    setIsSavingEdit(true);
    try {
      const result = await onUpdate(comment.id, nextContent);
      if (result.ok) {
        toast.success(copy.updated(isReply));
        setIsEditing(false);
      } else {
        toast.error(result.message || copy.updateError);
      }
    } finally {
      setIsSavingEdit(false);
    }
  };

  const hasAnyReactions =
    comment.id &&
    ((comment.reactionsCount && Object.keys(comment.reactionsCount).length > 0) ||
      localReactions.has(comment.id));

  return (
    <div
      key={comment.id || `comment-${level}`}
      id={comment.id ? `comment-${comment.id}` : undefined}
      data-comment-id={comment.id || undefined}
      className={cn(
        'w-full max-w-full min-w-0 break-words overflow-hidden box-border',
        indentClass,
      )}
    >
      <div className="flex flex-col gap-3 mb-3 sm:flex-row sm:items-start">
        <Avatar className="h-10 w-10 flex-shrink-0">
          <AvatarImage
            src={comment.author?.avatar?.url || comment.author?.avatar?.thumbnailURL || ''}
            alt={comment.author?.name || 'Аноним'}
          />
          <AvatarFallback className="bg-purple text-white text-sm font-medium">
            {getAuthorInitials(comment.author?.name || 'Аноним')}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <div className="min-w-0">
                <div className="font-medium text-foreground dark:text-white break-all sm:break-normal">
                  {comment.author?.name || 'Аноним'}
                </div>
                <div className="text-sm text-muted-foreground dark:text-[#898989]">
                  {comment.createdAt ? formatCommentDate(comment.createdAt) : 'недавно'}
                </div>
              </div>
              {comment.isEdited && comment.editedAt && (
                <Badge
                  variant="secondary"
                  className="text-xs bg-muted dark:bg-[#3a3f47] text-muted-foreground dark:text-[#898989]"
                >
                  <Edit3 className="h-3 w-3 mr-1" />
                  изменено
                </Badge>
              )}
            </div>

            {showOwnerMenu && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 shrink-0 p-0 text-muted-foreground dark:text-[#898989] hover:text-foreground dark:hover:text-white hover:bg-muted dark:hover:bg-[#3a3f47]"
                    aria-label={copy.actionsLabel(isReply)}
                    disabled={isDeleting || isSavingEdit}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  {canEdit && (
                    <DropdownMenuItem onClick={handleStartEdit} disabled={isEditing}>
                      <Edit3 className="mr-2 h-4 w-4" />
                      Редактировать
                    </DropdownMenuItem>
                  )}
                  {canDelete && (
                    <DropdownMenuItem
                      onClick={handleDeleteClick}
                      disabled={isDeleting}
                      className="text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Удалить
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {isEditing ? (
            <div className="space-y-2">
              <Textarea
                value={editContent}
                onChange={(event) => setEditContent(event.target.value)}
                rows={4}
                disabled={isSavingEdit}
                className="min-h-[96px] resize-y bg-background dark:bg-[#1B1D22]"
              />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={handleSaveEdit} disabled={isSavingEdit}>
                  {isSavingEdit ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Сохранить
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancelEdit}
                  disabled={isSavingEdit}
                >
                  Отмена
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-foreground dark:text-white mb-2 leading-relaxed whitespace-pre-wrap break-all sm:break-words [overflow-wrap:anywhere]">
              {comment.content || copy.unavailable(isReply)}
            </p>
          )}

          {hasAnyReactions && comment.id ? (
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {COMMENT_REACTIONS.map((reaction) => {
                const originalCount =
                  (comment.reactionsCount as Record<string, number> | undefined)?.[
                    reaction.value
                  ] || 0;
                const count = resolveReactionCount(comment.id, reaction.value, originalCount);
                if (count === 0) return null;

                const isUserReaction = currentUserReaction === reaction.value;

                return (
                  <button
                    key={reaction.value}
                    onClick={() =>
                      onReaction(
                        comment.id,
                        reaction.value,
                        currentUserReaction,
                        comment.reactionsCount as Record<string, number> | undefined,
                      )
                    }
                    className={`flex items-center gap-1 px-2 py-1 rounded-full transition-colors ${
                      isUserReaction
                        ? 'bg-primary/20 dark:bg-primary/20 border border-primary dark:border-primary'
                        : 'bg-muted dark:bg-[#3a3f47] hover:bg-muted/80 dark:hover:bg-[#3a3f47]/80'
                    } ${reactingComments.has(comment.id) ? 'opacity-90' : ''}`}
                  >
                    <span className="text-base">{reaction.emoji}</span>
                    <span className="text-sm text-muted-foreground dark:text-[#898989]">
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-1">
              <Popover
                open={comment.id ? openReactionPopovers.has(comment.id) : false}
                onOpenChange={(open) => {
                  if (!comment.id) return;
                  onReactionPopoverOpenChange(comment.id, open);
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-3 text-muted-foreground dark:text-[#898989] hover:text-foreground dark:hover:text-white hover:bg-muted dark:hover:bg-[#3a3f47]"
                    disabled={comment.id ? reactingComments.has(comment.id) : false}
                  >
                    {comment.id && reactingComments.has(comment.id) ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Heart className="h-4 w-4 mr-1" />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-2 bg-background dark:bg-[#1B1D22] border-border dark:border-[#3a3f47]">
                  <div className="flex gap-1">
                    {COMMENT_REACTIONS.map((reaction) => (
                      <button
                        key={reaction.value}
                        onClick={() =>
                          comment.id &&
                          onReaction(
                            comment.id,
                            reaction.value,
                            currentUserReaction,
                            comment.reactionsCount as Record<string, number> | undefined,
                          )
                        }
                        disabled={comment.id ? reactingComments.has(comment.id) : false}
                        className={`flex items-center justify-center w-10 h-10 rounded hover:bg-muted dark:hover:bg-[#3a3f47] transition-colors ${
                          comment.id && reactingComments.has(comment.id)
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }`}
                        title={reaction.emoji}
                      >
                        <span className="text-xl">{reaction.emoji}</span>
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              <Button
                variant="ghost"
                size="sm"
                onClick={handleReplyClick}
                className="h-8 px-3 text-muted-foreground dark:text-[#898989] hover:text-foreground dark:hover:text-white hover:bg-muted dark:hover:bg-[#3a3f47]"
              >
                <Reply className="h-4 w-4 mr-1" />
                Ответить
              </Button>

              {(comment.childrenCount?.totalDocs ?? 0) > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => comment.id && onLoadReplies(comment.id)}
                  className="h-8 px-3 text-muted-foreground dark:text-[#898989] hover:text-foreground dark:hover:text-white hover:bg-muted dark:hover:bg-[#3a3f47]"
                  disabled={comment.id ? repliesLoading.has(comment.id) : false}
                >
                  {comment.id && repliesLoading.has(comment.id) ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <MessageCircle className="h-4 w-4 mr-1" />
                  )}
                  {comment.id && expandedReplies.has(comment.id) ? 'Скрыть' : 'Показать'}{' '}
                  {comment.childrenCount?.totalDocs ?? 0}
                </Button>
              )}

              {canReport && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReportClick}
                  className="h-8 px-3 text-muted-foreground dark:text-[#898989] hover:text-red-500 dark:hover:text-red-400 hover:bg-muted dark:hover:bg-[#3a3f47]"
                >
                  <Flag className="h-4 w-4 mr-1" />
                  Пожаловаться
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {comment.id && replyingTo === comment.id && (
        <div className="mb-4">
          <CreateComment
            publicationKind={publicationKind}
            placeholder={copy.replyPlaceholder(comment.author?.name || 'пользователя')}
            className="mb-4"
            articleId={typeof articleId === 'string' ? parseInt(articleId) : articleId}
            postId={typeof postId === 'string' ? parseInt(postId) : postId}
            parentComment={comment.id}
            onSubmit={(newComment) => onReplySubmit(comment.id, newComment)}
          />
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onCancelReply}
              className="text-muted-foreground dark:text-[#898989] border-border dark:border-[#3a3f47] hover:bg-muted dark:hover:bg-[#3a3f47] hover:text-foreground dark:hover:text-white"
            >
              Отмена
            </Button>
          </div>
        </div>
      )}

      {comment.id && expandedReplies.has(comment.id) && loadedReplies.get(comment.id) && (
        <div className="mt-4 space-y-4 max-w-screen overflow-x-auto pr-2 box-border">
          {loadedReplies.get(comment.id)?.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              level={level + 1}
              articleId={articleId}
              postId={postId}
              replyingTo={replyingTo}
              expandedReplies={expandedReplies}
              repliesLoading={repliesLoading}
              loadedReplies={loadedReplies}
              reactingComments={reactingComments}
              localReactions={localReactions}
              openReactionPopovers={openReactionPopovers}
              resolveReactionCount={resolveReactionCount}
              resolveUserReaction={resolveUserReaction}
              onReply={onReply}
              onCancelReply={onCancelReply}
              onLoadReplies={onLoadReplies}
              onReplySubmit={onReplySubmit}
              onReaction={onReaction}
              onReactionPopoverOpenChange={onReactionPopoverOpenChange}
              onReport={onReport}
              onDelete={onDelete}
              onUpdate={onUpdate}
              publicationKind={publicationKind}
            />
          ))}
        </div>
      )}

      {canReport && onReport && (
        <CommentReportDialog
          open={reportDialogOpen}
          onOpenChange={setReportDialogOpen}
          title={copy.reportTitle}
          onSubmit={(category, reason, details) => onReport(comment.id, category, reason, details)}
        />
      )}
    </div>
  );
}
